/**
 * Client Portal — Reports page.
 * Download final assessment reports within the 30-day retention window.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button, LinearProgress,
  Stack,
} from '@mui/material';
import { Timer, Description, PictureAsPdf } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';
import PortalTooltip from '../../../components/PortalTooltip';

interface ReportItem {
  id: string;
  assessment_id: string;
  file_name: string | null;
  uploaded_at: string | null;
  expires_at: string | null;
  days_remaining: number;
  excel_available: boolean;
  pptx_available: boolean;
}

const Reports: React.FC = () => {
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Report id whose PDF is currently being prepared (background build + poll).
  const [preparing, setPreparing] = useState<string | null>(null);

  const fetchReports = useCallback(() => {

    setLoading(true);
    authFetch('/api/client/reports')
      .then((r) => r.json())
      .then((data) => { if (data.success) setReports(data.reports); })
      .catch(() => setError('Failed to load reports'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });

  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const triggerBrowserDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const filenameFrom = (res: Response, fileType: 'excel' | 'pptx' | 'pdf') => {
    const contentDisposition = res.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
    const fallbackExt = fileType === 'pptx' ? 'pptx' : fileType === 'pdf' ? 'pdf' : 'xlsx';
    return filenameMatch?.[1] || `report.${fallbackExt}`;
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleDownload = async (reportId: string, fileType: 'excel' | 'pptx' | 'pdf') => {
    // PDFs are built on demand in the background; poll until ready instead of
    // making the user click retry. Excel/PPTX are served immediately.
    if (fileType === 'pdf') {
      setError(null);
      setPreparing(reportId);
      const maxAttempts = 18; // ~3 minutes at 10s intervals
      try {
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          const res = await authFetch(`/api/client/reports/${reportId}/download?type=pdf`);
          if (res.status === 202) {
            await sleep(10_000);
            continue;
          }
          if (!res.ok) {
            const d = await res.json().catch(() => ({}));
            setError(d.error || 'PDF generation failed. Please try again.');
            return;
          }
          const blob = await res.blob();
          triggerBrowserDownload(blob, filenameFrom(res, 'pdf'));
          return;
        }
        setError('Your PDF is taking longer than expected. Please try again shortly.');
      } catch {
        setError('Failed to download the PDF. Please try again.');
      } finally {
        setPreparing(null);
      }
      return;
    }

    try {
      const res = await authFetch(`/api/client/reports/${reportId}/download?type=${fileType}`);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setError(errData.error || 'Download failed');
        return;
      }
      const blob = await res.blob();
      triggerBrowserDownload(blob, filenameFrom(res, fileType));
    } catch {
      setError('Failed to initiate download');
    }
  };

  const getRetentionColor = (days: number) => {
    if (days <= 3) return 'error';
    if (days <= 7) return 'warning';
    return 'success';
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <PortalTooltip tooltipKey="client.page.reports" showIcon>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Final Reports
          </Typography>
        </PortalTooltip>
        <RefreshButton onRefresh={fetchReports} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Download your finalized assessment reports. Reports are available for 30 days after delivery.
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Reports will be automatically removed after the retention period expires. Please download before the deadline.
      </Alert>

      {preparing && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 3 }}>
          Preparing your PDF… this can take up to a couple of minutes on the first download. The download will start automatically when it's ready.
          <LinearProgress sx={{ mt: 1 }} />
        </Alert>
      )}

      {reports.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No reports available for download.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Report</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Time Remaining</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.file_name || 'Assessment Report'}</TableCell>
                  <TableCell>
                    {r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Timer fontSize="small" color={getRetentionColor(r.days_remaining) as any} />
                      <Chip
                        label={`${r.days_remaining} days`}
                        size="small"
                        color={getRetentionColor(r.days_remaining) as any}
                      />
                      <LinearProgress
                        variant="determinate"
                        value={(r.days_remaining / 30) * 100}
                        sx={{ flex: 1, ml: 1, height: 6, borderRadius: 3 }}
                        color={getRetentionColor(r.days_remaining) as any}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<PictureAsPdf />}
                        onClick={() => handleDownload(r.id, 'pdf')}
                        title="Download PDF report"
                      >
                        PDF
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Description />}
                        onClick={() => handleDownload(r.id, 'excel')}
                        disabled={!r.excel_available}
                        title="Download Excel findings report"
                      >
                        Excel
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Reports;
